// React

// Components
import { gql, useMutation, useQuery } from "@apollo/client";
import {
  Card,
  CircularProgress,
  Drawer,
  Grid,
  Icon,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

// Data

// import { UserContext } from "context/user";
import CartelError from "components/Carteles/Error";
import SinDatos from "components/Carteles/SinDatos";
import SoftBadge from "components/SoftBadge";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import dayjs from "dayjs";
import { useContext, useEffect, useMemo, useState } from "react";
import { getMesaColor } from "utils/colors";
import ModalAdministrar from "./components/ModalAdministrar";
import ModalPedido from "./components/ModalPedido";
import PlanViewport from "./components/PlanViewport";

const MESA_STATUS_META = {
  LIBRE: {
    label: "Libres",
    color: "success",
    accent: "#58d68d",
  },
  ESPERANDO: {
    label: "Esperando",
    color: "warning",
    accent: "#f7dc6f",
  },
  OCUPADA: {
    label: "Ocupadas",
    color: "error",
    accent: "#d98880",
  },
};

const MOBILE_PLAN_ZOOM_MIN = 0.7;
const MOBILE_PLAN_ZOOM_MAX = 1.6;
const MOBILE_PLAN_ZOOM_STEP = 0.15;

const formatElapsedTime = (createdAt) => {
  const minutes = Math.max(dayjs().diff(dayjs(createdAt), "minute"), 0);

  if (minutes < 1) {
    return "Ahora";
  }

  if (minutes < 60) {
    return `${minutes} min`;
  }

  const hours = Math.floor(minutes / 60);
  const restMinutes = minutes % 60;

  if (restMinutes === 0) {
    return `${hours} h`;
  }

  return `${hours} h ${restMinutes} min`;
};

const getMesaDetalles = (mesa) => mesa?.pedido?.detalles || [];

const getMesaOldestDetail = (mesa) => {
  const detalles = getMesaDetalles(mesa);

  if (!detalles.length) {
    return null;
  }

  return detalles.reduce((oldest, detalle) => {
    if (!oldest) {
      return detalle;
    }

    return dayjs(detalle.createdAt).isBefore(dayjs(oldest.createdAt)) ? detalle : oldest;
  }, null);
};

const getMesaPendingCount = (mesa) =>
  getMesaDetalles(mesa).filter((detalle) => detalle.estado === "PENDIENTE").length;

const getMesaTotalItems = (mesa) =>
  getMesaDetalles(mesa).reduce((acc, detalle) => acc + Number(detalle.cantidad || 0), 0);

const getMesaMozoLabel = (mesa) => {
  const nombre = mesa?.pedido?.mozo?.nombre || "";
  const apellido = mesa?.pedido?.mozo?.apellido || "";

  return `${nombre} ${apellido}`.trim();
};

const formatMesaCountLabel = (count) => `${count} ${count === 1 ? "mesa" : "mesas"}`;
const normalizeMesaPosition = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? Math.round(numericValue) : 0;
};

function Areas() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const isTabletMesaDrawerSide = useMediaQuery(theme.breakpoints.up("md"));
  const [modalAdministrar, setModalAdministrar] = useState(false);
  const [areaElegida, setAreaElegida] = useState(0);
  const [selectedAreaId, setSelectedAreaId] = useState(null);
  const [areas, setAreas] = useState([]);
  const [mesas, setMesas] = useState([]);
  const [mesasTemporal, setMesasTemporal] = useState([]);
  const [moverHabilitado, setMoverHabilitado] = useState(false);
  const [mobilePlanZoom, setMobilePlanZoom] = useState(1);
  const [modalPedido, setModalPedido] = useState(false);
  const [mesaElegida, setMesaElegida] = useState(null);
  const [listadoPendientes, setListadoPendientes] = useState([]);
  const [tabletMesaDrawer, setTabletMesaDrawer] = useState({
    open: false,
    status: null,
    selectedMesaId: null,
  });
  const { handleSnackbar } = useContext(MessageManager);

  const { data, loading, error, refetch } = useQuery(
    gql`
      query areas {
        areas {
          id
          nombre
          orden
          file
          mesas {
            id
            numero
            posicionX
            posicionY
            idArea
            estado
            pedido {
              id
              paraLlevar
              comentario
              mozo {
                id
                nombre
                apellido
              }
              detalles {
                id
                createdAt
                cantidad
                estado
                numeroComanda
                precio
                horarioEntrega
                comentario
                motivoCancelacion
                impresionComanda
                producto {
                  id
                  nombre
                  seccion
                  comanderas {
                    id
                    printName
                    habilitado
                    estadoConexion
                  }
                  combo
                  esElaborado
                  precioCarta
                  precioOnline
                  precioPorMayor
                  precioPorMayorOnline
                  cantidadPorMayor
                }
                ingredientesQuitados {
                  id
                }
              }
            }
          }
        }
      }
    `,
    {
      fetchPolicy: "network-only",
    }
  );

  const [saveMesas, { loading: loadingSave }] = useMutation(
    gql`
      mutation saveMesas($input: [MesaInput]!) {
        saveMesas(input: $input) {
          id
        }
      }
    `
  );

  const handleSave = (mesasToPersist) => {
    const mesasToSave = Array.isArray(mesasToPersist) ? mesasToPersist : [mesasToPersist];
    Promise.all(
      mesasToSave.map((mesa) =>
        saveMesas({
          variables: {
            input: {
              id: mesa.id || null,
              posicionX: normalizeMesaPosition(mesa.posicionX),
              posicionY: normalizeMesaPosition(mesa.posicionY),
            },
          },
        })
      )
    )
      .then(() => {
        handleSnackbar("Posiciones guardadas correctamente", "success");
        refetch();
      })
      .catch(() => {
        handleSnackbar("Error al guardar posiciones", "error");
      });
  };

  const syncAreaSelection = (nextAreas, preferredAreaId = selectedAreaId) => {
    if (!nextAreas?.length) {
      setSelectedAreaId(null);
      setAreaElegida(0);
      setMesas([]);
      setMesasTemporal([]);
      return;
    }

    const preferredIndex =
      preferredAreaId !== null && preferredAreaId !== undefined
        ? nextAreas.findIndex((area) => Number(area.id) === Number(preferredAreaId))
        : -1;

    const nextIndex = preferredIndex >= 0 ? preferredIndex : 0;
    const nextArea = nextAreas[nextIndex];

    setSelectedAreaId(nextArea?.id ?? null);
    setAreaElegida(nextIndex);
    setMesas(nextArea?.mesas || []);
    setMesasTemporal(nextArea?.mesas || []);
  };

  useEffect(() => {
    if (data) {
      const sortedArray = [...data.areas]?.sort((a, b) => a.orden - b.orden) || [];
      setAreas(sortedArray);
      syncAreaSelection(sortedArray);
      let pendientes = [];
      sortedArray.forEach((area) => {
        area.mesas.forEach((mesa) => {
          if (mesa.pedido?.detalles) {
            mesa.pedido.detalles.forEach((detalle) => {
              if (detalle.estado === "PENDIENTE") {
                pendientes.push({
                  mesa: mesa.numero,
                  ...detalle,
                });
              }
            });
          }
        });
      });
      let sortedPendientes = pendientes.sort((a, b) => a.numeroComanda - b.numeroComanda);
      setListadoPendientes(sortedPendientes);
    }
  }, [data]);

  const handleOpenPedido = (mesa) => {
    setModalPedido(true);
    setMesaElegida(mesa);
  };

  const handleChangeArea = (index) => {
    const nextArea = areas[index];
    setAreaElegida(index);
    setSelectedAreaId(nextArea?.id ?? null);
    setMesas(nextArea?.mesas || []);
    setMesasTemporal(nextArea?.mesas || []);
    setMoverHabilitado(false);
    setMobilePlanZoom(1);
  };

  const changeMobileZoom = (delta) => {
    setMobilePlanZoom((prev) => {
      const nextZoom = Math.round((prev + delta) * 100) / 100;
      return Math.min(MOBILE_PLAN_ZOOM_MAX, Math.max(MOBILE_PLAN_ZOOM_MIN, nextZoom));
    });
  };

  const resetMobileZoom = () => {
    setMobilePlanZoom(1);
  };

  const areaActual = areas[areaElegida] || null;

  const resumenMesas = useMemo(
    () =>
      mesas.reduce(
        (acc, mesa) => {
          acc.total += 1;
          if (acc[mesa.estado] !== undefined) {
            acc[mesa.estado] += 1;
          } else {
            acc.otras += 1;
          }
          return acc;
        },
        {
          total: 0,
          LIBRE: 0,
          ESPERANDO: 0,
          OCUPADA: 0,
          otras: 0,
        }
      ),
    [mesas]
  );

  const pendienteMasAntiguo = useMemo(() => {
    if (!listadoPendientes.length) {
      return null;
    }

    return listadoPendientes.reduce((oldest, detalle) => {
      if (!oldest) {
        return detalle;
      }

      return dayjs(detalle.createdAt).isBefore(dayjs(oldest.createdAt)) ? detalle : oldest;
    }, null);
  }, [listadoPendientes]);

  const leyendaMesas = Object.entries(MESA_STATUS_META).map(([status, config]) => ({
    ...config,
    status,
    count: resumenMesas[status] || 0,
  }));

  const areaTienePlano = Boolean(areaActual?.file);
  const metricaPendientesMasAntiguo = pendienteMasAntiguo
    ? formatElapsedTime(pendienteMasAntiguo.createdAt)
    : "OK";

  const mesasEsperando = useMemo(
    () =>
      [...mesas]
        .filter((mesa) => mesa.estado === "ESPERANDO")
        .sort((mesaA, mesaB) => Number(mesaA.numero || 0) - Number(mesaB.numero || 0)),
    [mesas]
  );

  const mesasOcupadas = useMemo(
    () =>
      [...mesas]
        .filter((mesa) => mesa.estado === "OCUPADA")
        .sort((mesaA, mesaB) => Number(mesaA.numero || 0) - Number(mesaB.numero || 0)),
    [mesas]
  );

  const currentTabletDrawerMesas = useMemo(() => {
    if (tabletMesaDrawer.status === "ESPERANDO") {
      return mesasEsperando;
    }

    if (tabletMesaDrawer.status === "OCUPADA") {
      return mesasOcupadas;
    }

    return [];
  }, [mesasEsperando, mesasOcupadas, tabletMesaDrawer.status]);

  const selectedTabletDrawerMesa = useMemo(
    () =>
      currentTabletDrawerMesas.find((mesa) => mesa.id === tabletMesaDrawer.selectedMesaId) ||
      currentTabletDrawerMesas[0] ||
      null,
    [currentTabletDrawerMesas, tabletMesaDrawer.selectedMesaId]
  );

  const openTabletMesaDrawer = (status) => {
    const mesasByStatus = status === "ESPERANDO" ? mesasEsperando : mesasOcupadas;

    setTabletMesaDrawer({
      open: true,
      status,
      selectedMesaId: mesasByStatus[0]?.id || null,
    });
  };

  const closeTabletMesaDrawer = () => {
    setTabletMesaDrawer({
      open: false,
      status: null,
      selectedMesaId: null,
    });
  };

  const renderTabletDrawerTableAvatar = () => (
    <SoftBox
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
      sx={{
        width: 44,
        height: 44,
        borderRadius: "1rem",
        background: "linear-gradient(180deg, rgba(255, 238, 246, 0.96) 0%, rgba(255, 255, 255, 1) 100%)",
        border: "1px solid rgba(244, 114, 182, 0.16)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.95)",
      }}
    >
      <Icon sx={{ color: "#ec4899", fontSize: "1.2rem !important" }}>table_restaurant</Icon>
    </SoftBox>
  );

  const renderTabletDrawerTimePill = (createdAt) => {
    if (!createdAt) {
      return null;
    }

    return (
      <SoftBox
        px={1}
        py={0.45}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.55,
          borderRadius: "999px",
          backgroundColor: "#ffffff",
          border: "1px solid rgba(226, 232, 240, 0.95)",
          boxShadow: "0 8px 20px rgba(15, 23, 42, 0.05)",
        }}
      >
        <Icon sx={{ fontSize: "0.95rem !important", color: "#64748b" }}>schedule</Icon>
        <SoftTypography variant="caption" fontWeight="bold" sx={{ color: "#475569 !important" }}>
          {formatElapsedTime(createdAt)}
        </SoftTypography>
      </SoftBox>
    );
  };

  const renderAreaSelector = ({ compact = false, mobile = false, tablet = false } = {}) => (
    <SoftBox
      sx={{
        display: "flex",
        gap: tablet ? 0.5 : compact ? 0.65 : 0.85,
        flexWrap: mobile || tablet ? "nowrap" : "wrap",
        overflowX: mobile || tablet ? "auto" : "visible",
        pb: mobile || tablet ? 0.25 : 0,
        pr: mobile || tablet ? 0.5 : 0,
        p: mobile ? 0.3 : tablet ? 0.42 : compact ? 0.5 : 0.75,
        width: tablet ? "100%" : "auto",
        borderRadius: mobile ? "1rem" : tablet ? "1.15rem" : compact ? "1rem" : "1.2rem",
        backgroundColor: mobile
          ? "#ffffff"
          : tablet
            ? "rgba(44, 96, 154, 0.16)"
            : "rgba(255,255,255,0.12)",
        border: mobile
          ? "1px solid rgba(226, 232, 240, 0.95)"
          : tablet
            ? "1px solid rgba(44, 96, 154, 0.18)"
            : "1px solid rgba(255,255,255,0.18)",
        boxShadow: mobile ? "0 10px 24px rgba(15, 23, 42, 0.08)" : "none",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": {
          display: "none",
        },
      }}
    >
      {areas.map((area, index) => {
        const isActive = index === areaElegida;

        return (
          <SoftBox
            key={area.id || area.nombre}
            component="button"
            type="button"
            onClick={() => {
              handleChangeArea(index);
            }}
            sx={{
              appearance: "none",
              border: "none",
              cursor: "pointer",
              flex: "0 0 auto",
              px: tablet ? 1.2 : compact ? 1.25 : 1.5,
              py: mobile ? 0.72 : tablet ? 0.72 : compact ? 0.8 : 0.95,
              borderRadius: mobile ? "0.8rem" : tablet ? "0.95rem" : "1rem",
              transition: "all 180ms ease",
              backgroundColor: mobile
                ? isActive
                  ? "#2f6bff"
                  : "#ffffff"
                : tablet
                  ? isActive
                    ? "#ffffff"
                    : "rgba(44, 96, 154, 0.14)"
                  : isActive
                    ? "#ffffff"
                    : "rgba(255,255,255,0.08)",
              color: mobile
                ? isActive
                  ? "#ffffff"
                  : "#334155"
                : tablet
                  ? (isActive ? "#2c3087" : "#4a5d7a")
                  : isActive
                    ? "#2c3087"
                    : "#ffffff",
              boxShadow: isActive ? "0 10px 24px rgba(15, 23, 42, 0.14)" : "none",
              "&:hover": {
                backgroundColor: mobile
                  ? isActive
                    ? "#2f6bff"
                    : "#f8fafc"
                  : tablet
                    ? isActive
                      ? "#ffffff"
                      : "rgba(44, 96, 154, 0.22)"
                    : isActive
                      ? "#ffffff"
                      : "rgba(255,255,255,0.16)",
              },
              "&:focus-visible": {
                outline: mobile
                  ? "2px solid rgba(47, 107, 255, 0.35)"
                  : tablet
                    ? "2px solid rgba(44, 96, 154, 0.45)"
                    : "2px solid #ffffff",
                outlineOffset: 2,
              },
            }}
          >
            <SoftBox display="flex" alignItems="center" gap={0.9}>
              <SoftTypography
                variant={tablet || compact ? "caption" : "button"}
                fontWeight="bold"
                sx={{ color: "inherit !important", whiteSpace: "nowrap" }}
              >
                {area.nombre}
              </SoftTypography>
              <Icon
                sx={{
                  fontSize: tablet || compact ? "1rem !important" : "1.05rem !important",
                  color: "inherit",
                }}
              >
                {isActive ? "check_circle" : "radio_button_unchecked"}
              </Icon>
            </SoftBox>
          </SoftBox>
        );
      })}
    </SoftBox>
  );

  const renderMesaLegend = ({ mobile = false, compact = false } = {}) => (
    <SoftBox
      display="flex"
      gap={compact ? 0.6 : 0.8}
      flexWrap={compact ? "wrap" : mobile ? "nowrap" : "wrap"}
      overflow={mobile ? "auto" : "visible"}
      maxWidth="100%"
      pr={mobile ? 0.5 : 0}
    >
      {leyendaMesas.map((item) => (
        <SoftBox
          key={item.status}
          display="flex"
          alignItems="center"
          gap={0.8}
          px={compact ? 0.9 : mobile ? 1 : 1.2}
          py={compact ? 0.55 : mobile ? 0.7 : 0.8}
          sx={{
            flex: compact ? "1 1 auto" : "0 0 auto",
            borderRadius: "999px",
            backgroundColor: compact ? "rgba(255,255,255,0.92)" : "rgba(248, 250, 252, 0.95)",
            border: `1px solid ${item.accent}55`,
          }}
        >
          <SoftBox
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: getMesaColor(item.status),
              boxShadow: `0 0 0 4px ${item.accent}22`,
            }}
          />
          <SoftTypography variant="caption" fontWeight="bold">
            {item.label}
          </SoftTypography>
          <SoftBadge
            color={item.color}
            variant="gradient"
            size="xs"
            circular
            badgeContent={item.count}
          />
        </SoftBox>
      ))}
    </SoftBox>
  );

  const renderTabletCanvasStatus = () => {
    if (areaTienePlano) {
      return null;
    }

    return (
      <SoftBox
        px={1.1}
        py={0.7}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          borderRadius: "999px",
          backgroundColor: "rgba(255, 255, 255, 0.96)",
          border: "1px solid rgba(44, 48, 135, 0.12)",
        }}
      >
        <SoftTypography variant="caption" fontWeight="bold">
          Plano de área, arrastrar para navegar
        </SoftTypography>
      </SoftBox>
    );
  };

  const renderPlanoCanvas = ({ mobile = false, tablet = false } = {}) => (
    <PlanViewport
      areaActual={areaActual}
      areaTienePlano={areaTienePlano}
      mesas={mesas}
      mesasTemporal={mesasTemporal}
      moverHabilitado={moverHabilitado}
      setMesasTemporal={setMesasTemporal}
      handleOpenPedido={handleOpenPedido}
      zoom={mobile || tablet ? mobilePlanZoom : 1}
      mobile={mobile}
      tablet={tablet}
    />
  );

  const renderPlanZoomControls = ({ compact = false } = {}) => (
    <SoftBox display="flex" alignItems="center" gap={compact ? 0.45 : 0.55}>
      <SoftButton
        color="dark"
        iconOnly
        circular
        onClick={() => {
          changeMobileZoom(-MOBILE_PLAN_ZOOM_STEP);
        }}
        disabled={mobilePlanZoom <= MOBILE_PLAN_ZOOM_MIN}
        sx={{
          minWidth: `${compact ? 30 : 34}px !important`,
          width: compact ? 30 : 34,
          height: compact ? 30 : 34,
          backgroundColor: "#ffffff !important",
          color: "#334155 !important",
          border: "1px solid rgba(226, 232, 240, 0.95)",
          boxShadow: "0 10px 24px rgba(15, 23, 42, 0.08)",
        }}
      >
        <Icon>remove</Icon>
      </SoftButton>
      <SoftBox
        component="button"
        type="button"
        onClick={resetMobileZoom}
        sx={{
          appearance: "none",
          border: "1px solid rgba(226, 232, 240, 0.95)",
          backgroundColor: "#ffffff",
          color: "#334155",
          minWidth: compact ? 50 : 58,
          height: compact ? 30 : 34,
          px: compact ? 0.9 : 1.1,
          borderRadius: "0.95rem",
          fontSize: "0.75rem",
          fontWeight: 700,
          cursor: "pointer",
          boxShadow: "0 10px 24px rgba(15, 23, 42, 0.08)",
        }}
      >
        {`${Math.round(mobilePlanZoom * 100)}%`}
      </SoftBox>
      <SoftButton
        color="dark"
        iconOnly
        circular
        onClick={() => {
          changeMobileZoom(MOBILE_PLAN_ZOOM_STEP);
        }}
        disabled={mobilePlanZoom >= MOBILE_PLAN_ZOOM_MAX}
        sx={{
          minWidth: `${compact ? 30 : 34}px !important`,
          width: compact ? 30 : 34,
          height: compact ? 30 : 34,
          backgroundColor: "#ffffff !important",
          color: "#334155 !important",
          border: "1px solid rgba(226, 232, 240, 0.95)",
          boxShadow: "0 10px 24px rgba(15, 23, 42, 0.08)",
        }}
      >
        <Icon>add</Icon>
      </SoftButton>
    </SoftBox>
  );

  const renderPlanoCard = ({ mobile = false, tablet = false } = {}) => (
    <Card
      sx={{
        height: "100%",
        minWidth: mobile ? "82vw" : "auto",
        flex: mobile ? "0 0 82vw" : "1 1 auto",
        scrollSnapAlign: mobile ? "start" : "none",
        border: mobile ? "1px solid rgba(226, 232, 240, 0.95)" : "1px solid rgba(44, 48, 135, 0.08)",
        boxShadow: mobile ? "0 18px 34px rgba(15, 23, 42, 0.08)" : "0 18px 40px rgba(15, 23, 42, 0.08)",
        borderRadius: mobile ? "1.35rem" : undefined,
      }}
    >
      <SoftBox p={tablet ? 1.2 : mobile ? 1.5 : 2.5} height="100%">
        {tablet ? (
          <>
            <SoftBox mb={1}>
              <SoftBox display="flex" justifyContent="space-between" alignItems="center" gap={1} mb={0.75}>
                <SoftBox minWidth={0}>
                  <SoftTypography variant="button" fontWeight="bold" sx={{ color: "#0f172a !important" }}>
                    Plano de area
                  </SoftTypography>
                </SoftBox>
                {!isDesktop ? renderPlanZoomControls({ compact: true }) : null}
              </SoftBox>
              <SoftBox
                px={1}
                py={0.75}
                sx={{
                  borderRadius: "1rem",
                  backgroundColor: moverHabilitado ? "rgba(47, 107, 255, 0.08)" : "rgba(248, 250, 252, 0.96)",
                  border: "1px solid rgba(226, 232, 240, 0.95)",
                }}
              >
                <SoftBox display="flex" alignItems="center" justifyContent="space-between" gap={0.9} flexWrap="wrap">
                  {renderTabletCanvasStatus()}
                </SoftBox>
              </SoftBox>
            </SoftBox>
            {renderPlanoCanvas({ tablet: true })}
          </>
        ) : (
          <>
            <SoftBox
              mb={mobile ? 1.5 : 2.25}
              display="flex"
              flexDirection={mobile ? "column" : { xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={mobile ? "flex-start" : { xs: "flex-start", md: "center" }}
              gap={mobile ? 1.25 : 2}
            >
              {mobile ? (
                <SoftBox width="100%">
                  <SoftBox display="flex" justifyContent="space-between" alignItems="center" gap={1} mb={0.9}>
                    <SoftBox minWidth={0}>
                      <SoftTypography variant="button" fontWeight="bold" sx={{ color: "#0f172a !important" }}>
                        Plano de area
                      </SoftTypography>
                      <SoftTypography
                        variant="caption"
                        sx={{ color: "#64748b !important", display: "block", mt: 0.15 }}
                      >
                        {moverHabilitado
                          ? "Zoom activo para reubicar mesas"
                          : "Arrastra para navegar el plano"}
                      </SoftTypography>
                    </SoftBox>
                    {renderPlanZoomControls()}
                  </SoftBox>
                  <SoftBox
                    px={1}
                    py={0.8}
                    sx={{
                      borderRadius: "1rem",
                      backgroundColor: moverHabilitado ? "rgba(47, 107, 255, 0.08)" : "rgba(248, 250, 252, 0.96)",
                      border: "1px solid rgba(226, 232, 240, 0.95)",
                    }}
                  >
                    <SoftBox display="flex" alignItems="center" gap={0.8}>
                      <Icon
                        sx={{
                          color: moverHabilitado ? "#2f6bff" : "#64748b",
                          fontSize: "1rem !important",
                        }}
                      >
                        {moverHabilitado ? "zoom_out_map" : "pan_tool"}
                      </Icon>
                      <SoftTypography
                        variant="caption"
                        fontWeight="bold"
                        sx={{ color: "#334155 !important", lineHeight: 1.35 }}
                      >
                        {moverHabilitado
                          ? "Pan desactivado. Usa - y + para alejar o acercar antes de mover la mesa."
                          : "Pan activado. Puedes moverte por el plano y usar zoom cuando lo necesites."}
                      </SoftTypography>
                    </SoftBox>
                  </SoftBox>
                </SoftBox>
              ) : (
                <>
                  <SoftBox>
                    <SoftTypography variant={mobile ? "button" : "h6"} fontWeight="bold">
                      Plano del area
                    </SoftTypography>
                  </SoftBox>
                  {renderMesaLegend({ mobile })}
                </>
              )}
            </SoftBox>

            {moverHabilitado ? (
              <SoftBox
                mb={mobile ? 1.5 : 2}
                px={mobile ? 1.35 : 2}
                py={mobile ? 1 : 1.5}
                sx={{
                  borderRadius: "1rem",
                  background:
                    "linear-gradient(135deg, rgba(103, 193, 184, 0.16) 0%, rgba(255,255,255,1) 100%)",
                  border: "1px dashed rgba(20, 120, 119, 0.35)",
                }}
              >
                <SoftBox display="flex" alignItems="center" gap={1} mb={0.3}>
                  <Icon color="info" fontSize="small">
                    open_with
                  </Icon>
                  <SoftTypography variant="button" fontWeight="bold">
                    Modo mover activo
                  </SoftTypography>
                </SoftBox>
                {!mobile ? (
                  <SoftTypography variant="caption" display="block" color="text">
                    Arrastre las mesas a su nueva posicion y confirme para guardar el plano actualizado.
                  </SoftTypography>
                ) : null}
              </SoftBox>
            ) : null}

            {renderPlanoCanvas({ mobile })}
            {mobile ? (
              <SoftBox mt={1.15}>
                {renderMesaLegend({ mobile })}
              </SoftBox>
            ) : null}
          </>
        )}
      </SoftBox>
    </Card>
  );

  const renderTabletMetricPill = ({
    label,
    value,
    accent = "rgba(44, 48, 135, 0.12)",
    onClick,
    helper,
    icon,
    iconColor = "#2c3087",
    iconBackground = "rgba(44, 48, 135, 0.08)",
  }) => (
    <SoftBox
      key={label}
      component={onClick ? "button" : "div"}
      type={onClick ? "button" : undefined}
      onClick={onClick}
      px={1.05}
      py={0.8}
      sx={{
        minWidth: 0,
        width: "100%",
        minHeight: 82,
        appearance: "none",
        textAlign: "left",
        cursor: onClick ? "pointer" : "default",
        transition: "all 180ms ease",
        borderRadius: "0.95rem",
        backgroundColor: "#fff",
        border: `1px solid ${accent}`,
        boxShadow: "0 8px 18px rgba(15, 23, 42, 0.04)",
        ...(onClick
          ? {
            "&:hover": {
              borderColor: accent.replace("66", "aa"),
              boxShadow: "0 10px 22px rgba(15, 23, 42, 0.08)",
              transform: "translateY(-1px)",
            },
            "&:focus-visible": {
              outline: "2px solid rgba(44, 48, 135, 0.28)",
              outlineOffset: 2,
            },
          }
          : {}),
      }}
    >
      <SoftBox
        sx={{
          display: "grid",
          gridTemplateColumns: "34px minmax(0, 1fr) 24px",
          alignItems: "center",
          columnGap: 0.8,
          minHeight: "100%",
        }}
      >
        <SoftBox
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
          sx={{
            width: 34,
            height: 34,
            borderRadius: "999px",
            backgroundColor: iconBackground,
          }}
        >
          <Icon sx={{ fontSize: "1rem !important", color: iconColor }}>{icon}</Icon>
        </SoftBox>
        <SoftBox
          minWidth={0}
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <SoftTypography
            variant="caption"
            fontWeight="bold"
            color="text"
            sx={{
              display: "block",
              lineHeight: 1.1,
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </SoftTypography>
          <SoftTypography
            variant="button"
            fontWeight="bold"
            display="block"
            sx={{
              mt: 0.2,
              lineHeight: 1.05,
              whiteSpace: "nowrap",
            }}
          >
            {value}
          </SoftTypography>
          {helper ? (
            <SoftTypography
              variant="caption"
              color="text"
              sx={{
                display: "block",
                mt: 0.22,
                lineHeight: 1.2,
                whiteSpace: "nowrap",
              }}
            >
              {helper}
            </SoftTypography>
          ) : null}
        </SoftBox>
        {onClick ? (
          <SoftBox
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{
              width: 24,
              height: 24,
              borderRadius: "999px",
              backgroundColor: "rgba(44, 48, 135, 0.06)",
            }}
          >
            <Icon sx={{ fontSize: "0.95rem !important", color: "#2c3087" }}>chevron_right</Icon>
          </SoftBox>
        ) : (
          <SoftBox sx={{ width: 24, height: 24 }} />
        )}
      </SoftBox>
    </SoftBox>
  );

  const renderTabletMetricGrid = () => (
    <SoftBox
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
        gridAutoRows: "1fr",
        gap: 0.8,
        width: "100%",
        mt: 1,
      }}
    >
      {renderTabletMetricPill({
        label: "Mesas",
        value: resumenMesas.total,
        icon: "table_restaurant",
        iconColor: "#2f6bff",
        iconBackground: "rgba(47, 107, 255, 0.10)",
      })}
      {renderTabletMetricPill({
        label: "Libres",
        value: resumenMesas.LIBRE,
        accent: `${MESA_STATUS_META.LIBRE.accent}66`,
        icon: "check_circle",
        iconColor: "#16a34a",
        iconBackground: "rgba(22, 163, 74, 0.10)",
      })}
      {renderTabletMetricPill({
        label: "Esperando",
        value: resumenMesas.ESPERANDO,
        accent: `${MESA_STATUS_META.ESPERANDO.accent}66`,
        // helper: mesasEsperando.length > 0 ? formatMesaCountLabel(mesasEsperando.length) : "Sin mesas",
        onClick: mesasEsperando.length > 0 ? () => openTabletMesaDrawer("ESPERANDO") : undefined,
        icon: "schedule",
        iconColor: "#f59e0b",
        iconBackground: "rgba(245, 158, 11, 0.12)",
      })}
      {renderTabletMetricPill({
        label: "Ocupadas",
        value: resumenMesas.OCUPADA,
        accent: `${MESA_STATUS_META.OCUPADA.accent}66`,
        // helper: mesasOcupadas.length > 0 ? formatMesaCountLabel(mesasOcupadas.length) : "Sin mesas",
        onClick: mesasOcupadas.length > 0 ? () => openTabletMesaDrawer("OCUPADA") : undefined,
        icon: "groups",
        iconColor: "#ef4444",
        iconBackground: "rgba(239, 68, 68, 0.10)",
      })}
      {renderTabletMetricPill({
        label: "Mas antiguo",
        value: metricaPendientesMasAntiguo,
        icon: "hourglass_top",
        iconColor: "#64748b",
        iconBackground: "rgba(100, 116, 139, 0.12)",
      })}
    </SoftBox>
  );

  const renderTabletToolbar = () => (
    <Card
      sx={{
        border: "1px solid rgba(44, 48, 135, 0.08)",
        boxShadow: "0 14px 32px rgba(15, 23, 42, 0.08)",
      }}
    >
      <SoftBox p={1.25}>
        <SoftBox display="flex" alignItems="center" gap={0.75} flexWrap="wrap" mb={0.65}>
          <SoftTypography variant="caption" fontWeight="bold" sx={{ letterSpacing: "0.08em" }}>
            AREA
          </SoftTypography>
          <SoftTypography variant="caption" color="text">
            Seleccione el sector operativo
          </SoftTypography>
        </SoftBox>

        <SoftBox display="flex" alignItems="center" gap={1} flexWrap="nowrap">
          <SoftBox flex="1 1 auto" minWidth={0}>
            {renderAreaSelector({ compact: true, tablet: true })}
          </SoftBox>

          <SoftBox display="flex" alignItems="center" gap={0.75} flexShrink={0}>
            {!moverHabilitado ? (
              <Tooltip title="Mover mesas">
                <SoftButton
                  color="uproGreen"
                  iconOnly
                  circular
                  onClick={() => {
                    setMoverHabilitado(true);
                  }}
                  disabled={loadingSave}
                >
                  <Icon>open_with</Icon>
                </SoftButton>
              </Tooltip>
            ) : (
              <>
                <Tooltip title="Cancelar movimiento">
                  <SoftButton
                    color="primary"
                    iconOnly
                    circular
                    onClick={() => {
                      setMoverHabilitado(false);
                      setMesasTemporal(mesas);
                    }}
                  >
                    <Icon>close</Icon>
                  </SoftButton>
                </Tooltip>
                <Tooltip title="Guardar posiciones">
                  <SoftButton
                    color="uproGreen"
                    iconOnly
                    circular
                    onClick={() => {
                      setMoverHabilitado(false);
                      handleSave(mesasTemporal);
                    }}
                  >
                    <Icon>done</Icon>
                  </SoftButton>
                </Tooltip>
              </>
            )}
            <Tooltip title="Administrar areas">
              <SoftButton
                color="primary"
                iconOnly
                circular
                onClick={() => {
                  setModalAdministrar(true);
                }}
              >
                <Icon>tune</Icon>
              </SoftButton>
            </Tooltip>
          </SoftBox>
        </SoftBox>

        {moverHabilitado ? (
          <SoftBox mt={0.75}>
            <SoftBadge color="info" variant="gradient" badgeContent="Reubicando mesas" container />
          </SoftBox>
        ) : null}

        {renderTabletMetricGrid()}
      </SoftBox>
    </Card>
  );

  const renderTabletMesaDrawerList = () => (
    <SoftBox
      sx={{
        display: "grid",
        gap: 0.9,
      }}
    >
      {currentTabletDrawerMesas.map((mesa) => {
        const oldestDetail = getMesaOldestDetail(mesa);
        const mozoLabel = getMesaMozoLabel(mesa);
        const pendingCount = getMesaPendingCount(mesa);
        const totalItems = getMesaTotalItems(mesa);
        const isSelected = selectedTabletDrawerMesa?.id === mesa.id;

        return (
          <SoftBox
            key={mesa.id}
            component="button"
            type="button"
            onClick={() => {
              setTabletMesaDrawer((prev) => ({
                ...prev,
                selectedMesaId: mesa.id,
              }));
            }}
            sx={{
              appearance: "none",
              textAlign: "left",
              width: "100%",
              p: 1.15,
              borderRadius: "1.15rem",
              border: `1px solid ${isSelected ? "rgba(244, 114, 182, 0.55)" : "rgba(226, 232, 240, 0.95)"
                }`,
              background: isSelected
                ? "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(255, 244, 249, 0.96) 100%)"
                : "#ffffff",
              cursor: "pointer",
              transition: "all 180ms ease",
              boxShadow: isSelected
                ? "0 20px 40px rgba(236, 72, 153, 0.12)"
                : "0 12px 28px rgba(15, 23, 42, 0.05)",
              "&:hover": {
                backgroundColor: "#fff",
                borderColor: "rgba(244, 114, 182, 0.4)",
                transform: "translateY(-1px)",
              },
            }}
          >
            <SoftBox display="grid" gridTemplateColumns="44px minmax(0, 1fr)" gap={1}>
              {renderTabletDrawerTableAvatar()}
              <SoftBox minWidth={0}>
                <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={0.8} mb={0.35}>
                  <SoftBox minWidth={0}>
                    <SoftBox display="flex" alignItems="center" gap={0.7} flexWrap="wrap" mb={0.25}>
                      <SoftTypography variant="button" fontWeight="bold" sx={{ color: "#1e3a6d !important" }}>
                        Mesa {mesa.numero}
                      </SoftTypography>
                      <SoftBadge
                        color={MESA_STATUS_META[mesa.estado]?.color || "secondary"}
                        size="xs"
                        variant="gradient"
                        badgeContent={MESA_STATUS_META[mesa.estado]?.label || mesa.estado}
                      />
                    </SoftBox>
                    <SoftTypography
                      variant="caption"
                      color="text"
                      display="block"
                      sx={{ color: "#475569 !important", lineHeight: 1.45 }}
                    >
                      {totalItems > 0 ? `${totalItems} item(s)` : "Sin items cargados"}
                      {pendingCount > 0 ? ` - ${pendingCount} pendiente(s)` : ""}
                    </SoftTypography>
                    {mozoLabel ? (
                      <SoftTypography
                        variant="caption"
                        color="text"
                        display="block"
                        sx={{ color: "#64748b !important", mt: 0.15 }}
                      >
                        Mozo: {mozoLabel}
                      </SoftTypography>
                    ) : null}
                  </SoftBox>
                  {renderTabletDrawerTimePill(oldestDetail?.createdAt)}
                </SoftBox>
              </SoftBox>
            </SoftBox>
          </SoftBox>
        );
      })}
    </SoftBox>
  );

  const renderTabletMesaDetail = () => {
    if (!selectedTabletDrawerMesa) {
      return (
        <SoftBox
          display="flex"
          alignItems="center"
          justifyContent="center"
          minHeight={220}
          sx={{
            borderRadius: "1.2rem",
            border: "1px dashed rgba(148, 163, 184, 0.4)",
            backgroundColor: "rgba(255,255,255,0.6)",
          }}
        >
          <SoftTypography variant="caption" color="text">
            Seleccione una mesa para ver el detalle.
          </SoftTypography>
        </SoftBox>
      );
    }

    const mesa = selectedTabletDrawerMesa;
    const detalles = getMesaDetalles(mesa);
    const oldestDetail = getMesaOldestDetail(mesa);
    const mozoLabel = getMesaMozoLabel(mesa);
    const pendingCount = getMesaPendingCount(mesa);

    return (
      <Card
        sx={{
          boxShadow: "0 20px 44px rgba(15, 23, 42, 0.06)",
          border: "1px solid rgba(226, 232, 240, 0.95)",
          borderRadius: "1.35rem",
          backgroundColor: "#ffffff",
        }}
      >
        <SoftBox p={1.35}>
          <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} mb={1}>
            <SoftBox display="flex" alignItems="flex-start" gap={1} minWidth={0}>
              {renderTabletDrawerTableAvatar()}
              <SoftBox minWidth={0}>
                <SoftBox display="flex" alignItems="center" gap={0.7} flexWrap="wrap" mb={0.2}>
                  <SoftTypography variant="h5" fontWeight="bold" sx={{ color: "#1e3a6d !important" }}>
                    Mesa {mesa.numero}
                  </SoftTypography>
                  <SoftBadge
                    color={MESA_STATUS_META[mesa.estado]?.color || "secondary"}
                    size="xs"
                    variant="gradient"
                    badgeContent={MESA_STATUS_META[mesa.estado]?.label || mesa.estado}
                  />
                </SoftBox>
                {mozoLabel ? (
                  <SoftTypography variant="caption" color="text" display="block" sx={{ color: "#64748b !important" }}>
                    Mozo: {mozoLabel}
                  </SoftTypography>
                ) : null}
                {mesa?.pedido?.comentario ? (
                  <SoftTypography variant="caption" color="text" display="block" sx={{ color: "#64748b !important" }}>
                    Comentario: {mesa.pedido.comentario}
                  </SoftTypography>
                ) : null}
              </SoftBox>
            </SoftBox>
            {renderTabletDrawerTimePill(oldestDetail?.createdAt)}
          </SoftBox>

          <SoftBox
            mb={1.1}
            p={1.1}
            sx={{
              borderRadius: "1.1rem",
              border: "1px solid rgba(226, 232, 240, 0.95)",
              background: "linear-gradient(180deg, rgba(247, 250, 255, 0.98) 0%, rgba(255,255,255,1) 100%)",
            }}
          >
            <Grid container spacing={0}>
              <Grid item xs={4}>
                <SoftBox px={1.15} py={0.35}>
                  <SoftBox display="flex" alignItems="center" gap={0.55} mb={0.18}>
                    <Icon sx={{ fontSize: "0.95rem !important", color: "#64748b" }}>inventory_2</Icon>
                    <SoftTypography variant="caption" fontWeight="bold" color="text">
                      Items
                    </SoftTypography>
                  </SoftBox>
                  <SoftTypography variant="h5" display="block" fontWeight="bold" sx={{ color: "#1e3a6d !important" }}>
                    {getMesaTotalItems(mesa)}
                  </SoftTypography>
                </SoftBox>
              </Grid>
              <Grid item xs={4}>
                <SoftBox
                  px={1.15}
                  py={0.35}
                  sx={{
                    borderLeft: "1px solid rgba(226, 232, 240, 0.95)",
                    borderRight: "1px solid rgba(226, 232, 240, 0.95)",
                  }}
                >
                  <SoftBox display="flex" alignItems="center" gap={0.55} mb={0.18}>
                    <Icon sx={{ fontSize: "0.95rem !important", color: "#64748b" }}>assignment_late</Icon>
                    <SoftTypography variant="caption" fontWeight="bold" color="text">
                      Pendientes
                    </SoftTypography>
                  </SoftBox>
                  <SoftTypography variant="h5" display="block" fontWeight="bold" sx={{ color: "#1e3a6d !important" }}>
                    {pendingCount}
                  </SoftTypography>
                </SoftBox>
              </Grid>
              <Grid item xs={4}>
                <SoftBox px={1.15} py={0.35}>
                  <SoftBox display="flex" alignItems="center" gap={0.55} mb={0.18}>
                    <Icon sx={{ fontSize: "0.95rem !important", color: "#64748b" }}>receipt_long</Icon>
                    <SoftTypography variant="caption" fontWeight="bold" color="text">
                      Detalles
                    </SoftTypography>
                  </SoftBox>
                  <SoftTypography variant="h5" display="block" fontWeight="bold" sx={{ color: "#1e3a6d !important" }}>
                    {detalles.length}
                  </SoftTypography>
                </SoftBox>
              </Grid>
            </Grid>
          </SoftBox>

          <SoftBox sx={{ display: "grid", gap: 0.95 }}>
            {detalles.length > 0 ? (
              detalles.map((detalle, index) => (
                <SoftBox
                  key={detalle.id || index}
                  px={1.05}
                  py={0.95}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "42px minmax(0, 1fr)",
                    gap: 0.9,
                    borderRadius: "1rem",
                    backgroundColor: "#fff",
                    border: "1px solid rgba(226, 232, 240, 0.95)",
                    boxShadow: "0 12px 26px rgba(15, 23, 42, 0.04)",
                  }}
                >
                  <SoftBox
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: "999px",
                      background: "linear-gradient(180deg, rgba(255, 238, 246, 0.96) 0%, rgba(255,255,255,1) 100%)",
                      border: "1px solid rgba(244, 114, 182, 0.16)",
                    }}
                  >
                    <Icon sx={{ color: "#ec4899", fontSize: "1rem !important" }}>local_cafe</Icon>
                  </SoftBox>
                  <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={0.75} minWidth={0}>
                    <SoftBox minWidth={0}>
                      <SoftTypography variant="button" display="block" fontWeight="bold" sx={{ color: "#1e3a6d !important" }}>
                        {detalle.producto.nombre} x{detalle.cantidad}
                      </SoftTypography>
                      <SoftTypography variant="caption" color="text" display="block" sx={{ color: "#64748b !important" }}>
                        Comanda #{detalle.numeroComanda || "-"} · {dayjs(detalle.createdAt).format("HH:mm")}
                      </SoftTypography>
                    </SoftBox>
                    <SoftBadge
                      color={detalle.estado === "PENDIENTE" ? "warning" : "info"}
                      size="xs"
                      variant="gradient"
                      badgeContent={detalle.estado}
                    />
                  </SoftBox>
                </SoftBox>
              ))
            ) : (
              <SoftTypography variant="caption" color="text">
                No hay detalles cargados para esta mesa.
              </SoftTypography>
            )}
          </SoftBox>

          <SoftButton
            color="primary"
            circular
            fullWidth
            sx={{
              mt: 1.35,
              minHeight: 48,
              background: "linear-gradient(90deg, #ff0f7b 0%, #f4048e 100%) !important",
              boxShadow: "0 20px 36px rgba(244, 63, 154, 0.3)",
              color: "#ffffff !important",
            }}
            onClick={() => {
              closeTabletMesaDrawer();
              handleOpenPedido(mesa);
            }}
          >
            <Icon>visibility</Icon>
            &nbsp;Abrir pedido
          </SoftButton>
        </SoftBox>
      </Card>
    );
  };

  const renderTabletMesaDrawer = () => (
    <Drawer
      anchor={isTabletMesaDrawerSide ? "right" : "bottom"}
      open={tabletMesaDrawer.open}
      onClose={closeTabletMesaDrawer}
      PaperProps={{
        sx: {
          width: isTabletMesaDrawerSide ? "min(92vw, 980px)" : "100%",
          maxWidth: "100%",
          height: isTabletMesaDrawerSide ? "100dvh" : "auto",
          maxHeight: isTabletMesaDrawerSide ? "100dvh" : "82vh",
          borderTopLeftRadius: isTabletMesaDrawerSide ? "1.75rem" : "1.75rem",
          borderTopRightRadius: 0,
          borderBottomLeftRadius: isTabletMesaDrawerSide ? "1.75rem" : 0,
          borderBottomRightRadius: 0,
          background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
          borderTop: isTabletMesaDrawerSide ? "none" : "1px solid rgba(226, 232, 240, 0.95)",
          borderLeft: isTabletMesaDrawerSide ? "1px solid rgba(226, 232, 240, 0.95)" : "none",
          boxShadow: isTabletMesaDrawerSide
            ? "-24px 0 64px rgba(15, 23, 42, 0.14)"
            : "0 -24px 64px rgba(15, 23, 42, 0.14)",
        },
      }}
    >
      <SoftBox p={1.5} height="100%" display="flex" flexDirection="column">
        {!isTabletMesaDrawerSide ? (
          <SoftBox
            mx="auto"
            mb={1.2}
            sx={{
              width: 54,
              height: 5,
              borderRadius: "999px",
              backgroundColor: "rgba(148, 163, 184, 0.4)",
            }}
          />
        ) : null}
        <SoftBox
          mb={1.5}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
        >
          <SoftBox display="flex" alignItems="flex-start" gap={1.1} minWidth={0}>
            <SoftBox
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{
                width: 44,
                height: 44,
                borderRadius: "1rem",
                background: "linear-gradient(180deg, rgba(255, 238, 246, 0.96) 0%, rgba(255,255,255,1) 100%)",
                border: "1px solid rgba(244, 114, 182, 0.16)",
              }}
            >
              <Icon sx={{ color: "#ec4899", fontSize: "1.15rem !important" }}>
                {tabletMesaDrawer.status === "ESPERANDO" ? "groups" : "table_bar"}
              </Icon>
            </SoftBox>
            <SoftBox minWidth={0}>
              <SoftTypography variant="h4" fontWeight="bold" sx={{ color: "#1e3a6d !important", lineHeight: 1.05 }}>
                {tabletMesaDrawer.status === "ESPERANDO" ? "Mesas en espera" : "Mesas ocupadas"}
              </SoftTypography>
              <SoftTypography variant="button" color="text" sx={{ color: "#64748b !important", display: "block", mt: 0.28 }}>
                Seleccione una mesa de {areaActual?.nombre || "esta area"} para ver el detalle.
              </SoftTypography>
            </SoftBox>
          </SoftBox>
          <SoftButton
            color="primary"
            iconOnly
            circular
            onClick={closeTabletMesaDrawer}
            sx={{
              width: 44,
              height: 44,
              minWidth: "44px !important",
              background: "linear-gradient(180deg, #ff0f7b 0%, #f4048e 100%) !important",
              boxShadow: "0 18px 30px rgba(244, 63, 154, 0.28)",
              color: "#ffffff !important",
            }}
          >
            <Icon>close</Icon>
          </SoftButton>
        </SoftBox>

        <SoftBox
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "0.92fr 1.22fr" },
            gap: 1.25,
            flex: 1,
            minHeight: 0,
          }}
        >
          <SoftBox
            sx={{
              minHeight: 0,
              overflowY: "auto",
              pr: 0.45,
              pt: 0.1,
              "&::-webkit-scrollbar": {
                width: 6,
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "rgba(203, 213, 225, 0.9)",
                borderRadius: "999px",
              },
            }}
          >
            {renderTabletMesaDrawerList()}
          </SoftBox>
          <SoftBox
            sx={{
              minHeight: 0,
              overflowY: "auto",
              pr: 0.35,
              p: 0.2,
              borderRadius: "1.45rem",
              background:
                "linear-gradient(180deg, rgba(247, 250, 255, 0.88) 0%, rgba(255,255,255,0.98) 100%)",
              "&::-webkit-scrollbar": {
                width: 6,
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "rgba(203, 213, 225, 0.9)",
                borderRadius: "999px",
              },
            }}
          >
            {renderTabletMesaDetail()}
          </SoftBox>
        </SoftBox>
      </SoftBox>
    </Drawer>
  );

  return (
    <Card
      sx={{
        overflow: "hidden",
        border: "1px solid rgba(44, 48, 135, 0.08)",
        boxShadow: "0 24px 60px rgba(15, 23, 42, 0.08)",
        minHeight: "calc(100dvh - 10rem)",
      }}
    >
      <Grid container spacing={1.5} p={1.5} sx={{ minHeight: "calc(100dvh - 10rem)" }}>
        {loading ? (
          <Grid xs={12} item display="flex" justifyContent="center" py={10}>
            <CircularProgress />
          </Grid>
        ) : error ? (
          <Grid xs={12} item>
            <CartelError />
          </Grid>
        ) : areas?.length > 0 ? (
          <>
            <Grid item xs={12}>
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  {renderTabletToolbar()}
                </Grid>
                <Grid item xs={12}>
                  {renderPlanoCard({ tablet: true })}
                </Grid>
              </Grid>
            </Grid>
          </>
        ) : (
          <SinDatos
            titulo="No hay areas"
            mensaje="No se han encontrado areas en el sistema"
            icono="zoom_out_map"
          />
        )}
      </Grid>
      <ModalAdministrar
        open={modalAdministrar}
        handleClose={() => {
          setModalAdministrar(false);
        }}
        refetch={refetch}
        data={areas}
        onAreaSaved={(nextAreaId) => {
          setSelectedAreaId(nextAreaId);
        }}
      />
      <ModalPedido
        open={modalPedido}
        handleClose={(event, reason) => {
          if (reason == "backdropClick") {
            return;
          }
          setModalPedido(false);
          setMesaElegida(null);
        }}
        mesaElegida={mesaElegida}
        refetch={refetch}
      />
      {renderTabletMesaDrawer()}
    </Card>
  );
}

export default Areas;
